import React, { useEffect, useState } from 'react';
import { Modal, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { WindowInformation } from '../../../services/window-information';
import FormInput from '../../../components/FormInput';
import { translate } from '../../../services/translate';
import FormActions from '../../../components/FormActions';
import { platformSettingsService } from '../../../services/central-api/platform-settings';
import ActingBodiesDropdown from '../../../components/access/ActingBodiesDropdown';
import Toast from 'react-native-toast-message';
import OccurrenceTypeDropdown from '../../../components/access/OccurrenceTypeDropdown';

export default function EventIntegrationsModal({ onClose, eventIntegration, authenticatedUser, isModalVisible }: { onClose: () => void; eventIntegration?: EventsIntegration; authenticatedUser?: AuthenticatedUser | null; isModalVisible: boolean; }) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [name, setName] = useState(eventIntegration?.triggerType || '');
    const [actingBodyId, setActingBodyId] = useState<number>(eventIntegration?.actingBody?.id ?? 0);
    const [occurrenceTypeId, setOccurrenceTypeId] = useState<number>(eventIntegration?.occurrenceType?.id ?? 0);

    function isNameInvalid(value: string) {
        return value == '';
    }

    function isFormValid() {
        return !isNameInvalid(name);
    }

    function clearValues() {
        setName('');
        setActingBodyId(0);
        setOccurrenceTypeId(0);
    }

    async function loadPageInfo() {
        if (!authenticatedUser) {
            return;
        }
        if (eventIntegration) {
            setName(eventIntegration.triggerType);
            setActingBodyId(eventIntegration.actingBody?.id ?? 0);
            setOccurrenceTypeId(eventIntegration.occurrenceType?.id ?? 0);
        }
    }

    useEffect(() => {
        if (isModalVisible) {
            loadPageInfo();
        }
    }, [isModalVisible]);

    useEffect(() => {
        if (actingBodyId == 0) {
            setOccurrenceTypeId(0);
        }
    }, [actingBodyId]);

    async function formSubmit() {
        try {
            setIsLoading(true);
            if (eventIntegration?.id) {
                await platformSettingsService.updateEventIntegration({
                    id: eventIntegration.id,
                    actingBodyId: actingBodyId || null,
                    occurrenceTypeId: occurrenceTypeId || null,
                });
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                onClose();
                return;
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onClose();
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <Modal
            transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
                clearValues();
            }}
        >
            <View style={styles.modalContainer}>
                <View style={styles.centeredView}>
                    <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '20%' }]}>
                        <FormInput
                            label={translate('name')}
                            placeholder={translate('name')}
                            value={translate(name)}
                            onChangeText={setName}
                            invalid={isNameInvalid}
                            disabled={true}
                        />

                        {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.access_edit_platform_settings) &&
                            <ActingBodiesDropdown includeAllOption={false} allowEmpty={true} value={actingBodyId} setValue={setActingBodyId} zIndex={3} />
                        }

                        {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.access_edit_platform_settings) && (actingBodyId != 0) &&
                            <OccurrenceTypeDropdown actingBodyId={actingBodyId} allowEmpty={true} setValue={setOccurrenceTypeId} value={occurrenceTypeId} zIndex={2} />
                        }

                        <FormActions
                            isLoading={isLoading}
                            onSubmit={formSubmit}
                            onClose={() => {
                                onClose();
                                clearValues();
                            }}
                            disabled={!isFormValid()} />
                    </View>
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    modalContainer: {
        flex: 1,
        backgroundColor: '#31313199'
    },
    centeredView: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    buttonsContainer: {
        flexDirection: 'row',
        alignItems: 'flex-end',
        gap: 10,
    },
}));
